// useProductHandlers.ts
import { useState } from "react";
import { Product } from "../types/productTypes";
import { useProducts } from "./useProducts";
import { useCategories } from "./useCategories";
import { useStaff } from "./useStaff";
import api from "../../../../../features/auth/axiosConfig";
import { notifyError, notifySuccess } from "../../../../../assets/toastService";
import { useUser } from "src/business-types/grocery/components/UserContext";

interface UseProductHandlersProps {
  businessType: string;
}

export const useProductHandlers = ({ businessType }: UseProductHandlersProps) => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showDeletedProducts, setShowDeletedProducts] = useState(false);
  const [page, setPage] = useState(0);
  const{userConfigration}=useUser()
  const [rowsPerPage, setRowsPerPage] = useState(userConfigration?.tableDefultSize || 10);

  const { categories } = useCategories();
  const { staff } = useStaff();
  const { products, totalRecords, loading, refetchProducts } = useProducts(showDeletedProducts, page, rowsPerPage, searchTerm, categoryFilter);

  // ADD PRODUCT
  const handleAddProduct = async (formData: any) => {
    try {
      const selectedCategory = categories.find((cat) => cat._id.$oid === formData.categoryId);

      const payload = new FormData();
      payload.append("ProductName", formData.productName);
      payload.append("Amount", formData.amount);
      payload.append("ArabicName", formData.arabicName);
      payload.append("own_product", String(!formData.ownProduct));
      payload.append("category_id", formData.categoryId);
      payload.append("categoryName", selectedCategory?.CategoryName || "");
      payload.append("staff_list", JSON.stringify(formData.staffList || []));

      if (formData.type) payload.append("Type", formData.type);
      if (formData.image) payload.append("image", formData.image);

      await api.post("/app/v1/pos/product", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      notifySuccess("Product added successfully");

      setShowAddModal(false);
      refetchProducts();
    } catch (err: any) {
      const message = err?.response?.data?.message || "Something went wrong";

      notifyError(message);
    }
  };

  const handleEditProduct = (product: Product) => {
    setSelectedProduct(product);
    setShowEditModal(true);
  };

  const handleDeleteProduct = (product: Product) => {
    setSelectedProduct(product);
    setShowDeleteModal(true);
  };

  const handleCloseModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedProduct(null);
  };

  const handleCategoryChange = (value: string) => {
    setCategoryFilter(value);
    setPage(0);
  };

  const handleToggleDeleted = () => {
    setShowDeletedProducts((prev) => !prev);
    setPage(0);
    setSearchTerm("");
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(0);
  };

  const handlePageChange = (_event: any, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setPage(0);
  };

  return {
    products,
    categories,
    staff,
    totalRecords,
    loading,
    selectedProduct,
    showAddModal,
    showEditModal,
    showDeleteModal,
    searchTerm,
    categoryFilter,
    showDeletedProducts,
    page,
    rowsPerPage,
    refetchProducts,
    setShowAddModal,
    handleAddProduct,
    handleEditProduct,
    handleDeleteProduct,
    handleCloseModals,
    handleCategoryChange,
    handleToggleDeleted,
    handleSearchChange,
    handlePageChange,
    handleRowsPerPageChange,
  };
};
